/**
 * sandboxd を呼ぶカードの実行状態（実行中・再試行の待機・エラー）。
 * 同時実行は 1（sandboxd-client.md §4.2）。カードを離れたら dispose() で中止する。
 */
import { describeRunError, type RunHooks } from './run';

export class RunState {
  running = $state(false);
  /** 再試行の待機中（sandboxd-client.md §4.4）。残り秒は remaining */
  retry = $state<{ attempt: 1 | 2; until: number } | null>(null);
  now = $state(Date.now());
  error = $state<{ message: string; retry: boolean } | null>(null);

  #controller: AbortController | null = null;
  #timer: ReturnType<typeof setInterval> | null = null;

  get remaining(): number {
    if (!this.retry) return 0;
    return Math.max(0, Math.ceil((this.retry.until - this.now) / 1000));
  }

  /** 待機中に出す文言（copy-guidelines.md §4「判定中・混雑」） */
  get retryMessage(): string | null {
    if (!this.retry) return null;
    return `混み合っています。${this.remaining} 秒後にもう一度送ります（${this.retry.attempt}/2）。`;
  }

  /** 実行する。結果か、表示しないエラー（中止など）のとき null */
  async start<T>(fn: (hooks: RunHooks) => Promise<T>): Promise<T | null> {
    if (this.running) return null;
    this.#controller = new AbortController();
    this.running = true;
    this.error = null;
    this.retry = null;
    try {
      return await fn({
        signal: this.#controller.signal,
        onRetry: (attempt, waitMs) => {
          this.now = Date.now();
          this.retry = { attempt, until: this.now + waitMs };
          this.#tick();
        },
      });
    } catch (e) {
      this.error = describeRunError(e);
      return null;
    } finally {
      this.running = false;
      this.retry = null;
      this.#stopTimer();
      this.#controller = null;
    }
  }

  /** 実行・待機をやめる（AbortError になり、エラーは出さない） */
  cancel(): void {
    this.#controller?.abort();
  }

  dispose(): void {
    this.cancel();
    this.#stopTimer();
  }

  #tick(): void {
    this.#stopTimer();
    this.#timer = setInterval(() => {
      this.now = Date.now();
      if (this.remaining <= 0) this.#stopTimer();
    }, 250);
  }

  #stopTimer(): void {
    if (this.#timer !== null) clearInterval(this.#timer);
    this.#timer = null;
  }
}
